/* eslint-disable array-callback-return */
import _ from "lodash";
import { store } from "..";
import { TYPES } from "../data/hospital.data";
import {
	setFacilityPercentage as setFacilityPercentageComparison,
	setFilterTotal as setFilterTotalComparison,
	setIsCensusTotalLocked as setIsCensusTotalLockedComparison,
	setLockedByFacility as setLockedByFacilityComparison,
	setLockedTotalBy as setLockedTotalByComparison,
	setLockTotal as setLockTotalComparison,
} from "../store/reducers/comparisonReducers/deceasedComparison.slice";
import {
	setFacilityPercentage,
	setFilterTotal,
	setIsCensusTotalLocked,
	setLockedByFacility,
	setLockedTotalBy,
	setLockTotal,
} from "../store/reducers/deceased.slice";
import { TOTAL_TYPE, CUSTOM_TAB_TYPE } from "../types/common.type";
import { DECEASED_CARDS_TYPE } from "../types/deceased.type";
import { dynamicCardFilter, filterCustomPatientData, getDynamicPercentageBy, processDynamicCard, updateListTotalValue } from "./common";
import { batch } from "react-redux";
import { getCustomTabsCards } from "./custom-tab-filter";
import { daysData, doctorData, floorsData, insuranceData } from "./hospital-common";
import { PAGE_TYPE } from "../types/pages.type";

// Attach level1/level2/level3/level4/level5 totals to list items for tooltip only (non-mutating calculations).
const attachLevelTotals = (list, snapshots, currentLevel, getKey, cardTitles = {}) => {
	if (!Array.isArray(list) || currentLevel < 2 || !snapshots) return list;
	const level1 = Array.isArray(snapshots.level1) ? snapshots.level1 : [];
	const level2 = Array.isArray(snapshots.level2) ? snapshots.level2 : [];
	const level3 = Array.isArray(snapshots.level3) ? snapshots.level3 : [];
	const level4 = Array.isArray(snapshots.level4) ? snapshots.level4 : [];
	const level5 = Array.isArray(snapshots.level5) ? snapshots.level5 : [];
	return list.map((item) => {
		// Preserve existing level totals if they're already set (e.g., from dxData, shiftData)
		if (item.level1Total !== undefined || item.level2Total !== undefined || item.level3Total !== undefined || item.level4Total !== undefined || item.level5Total !== undefined) {
			return {
				...item,
				...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
			};
		}
		const key = getKey(item);
		if (key === undefined || key === null) return item;
		const level1Total = level1.filter((p) => getKey(p) === key).length;
		const level2Total = level2.filter((p) => getKey(p) === key).length;
		const level3Total = level3.filter((p) => getKey(p) === key).length;
		const level4Total = level4.filter((p) => getKey(p) === key).length;
		const level5Total = level5.filter((p) => getKey(p) === key).length;
		return {
			...item,
			...(currentLevel >= 2 && { level1Total }),
			...(currentLevel >= 3 && { level2Total }),
			...(currentLevel >= 4 && { level3Total }),
			...(currentLevel >= 5 && { level4Total }),
			...(currentLevel >= 6 && { level5Total }),
			...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
			...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
			...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
			...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
			...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
		};
	});
};

// Helper to attach level totals when result is an array; keeps original data intact.
const maybeAttachTotals = (dataArr, snapshots, currentLevel, keySelector, cardTitles = {}) => {
	if (!Array.isArray(dataArr)) return dataArr;
	return attachLevelTotals(dataArr, snapshots, currentLevel, keySelector, cardTitles);
};

export async function plannedFilter(patientData, oldFilter, type = null) {
	if (!Array.isArray(patientData) || !oldFilter) return [];

	const filterKeys = [
		{ key: "insuranceData", idKey: "insuranceId" },
		{ key: "doctorData", idKey: "doctorId" },
		{ key: "floorsData", idKey: "floorId" }
	];

	return patientData.filter(patient => {
		return filterKeys.every(({ key, idKey }) => {
			// Skip this filter if it's the excluded type
			if (type === key) return true;
			const filterArr = oldFilter[key];
			// If filter is not set or empty, don't filter by this key
			if (!Array.isArray(filterArr) || filterArr.length === 0) return true;
			return filterArr.includes(patient[idKey]);
		});
	});
}

// * added comparison
export async function updateFacilityPercentageTotal(data, forComparison) {
	if (!Array.isArray(data) || data.length === 0) {
		const emptyResult = [];
		if (forComparison) {
			store.dispatch(setFacilityPercentageComparison(emptyResult));
		} else {
			store.dispatch(setFacilityPercentage(emptyResult));
		}
		return;
	}

	const facilityWiseTotal = Object.values(
		data.reduce((acc, item, idx) => {
			const facilityId = item?.facilityId;
			if (facilityId == null) return acc;
			if (!acc[facilityId]) {
				acc[facilityId] = { id: facilityId, total: 0, firstIndex: idx };
			}
			acc[facilityId].total += 1;
			return acc;
		}, {})
	)
	.sort((a, b) => a.firstIndex - b.firstIndex)
	.map(({ id, total }) => ({ id, total }));

	if (forComparison) {
		store.dispatch(setFacilityPercentageComparison(facilityWiseTotal));
	} else {
		store.dispatch(setFacilityPercentage(facilityWiseTotal));
	}
}

// * added comparison
export async function updateFilterListData(cardFilter, patientList, priorityData = [], forComparison) {
	let objectCustom = {};
	let newSavedFilters = [];
	const safePatientList = patientList || {};
	const baseList = Array.isArray(safePatientList.list) ? safePatientList.list : [];
	let patientFilterData = baseList;
	const diffDashboardPatients = safePatientList?.diffDashboardPatients ?? [];
	const {
		dbData,
		lockedTotal,
		transferType,
		lockedTotalBy
	} = forComparison ? store.getState().deceasedComparison : store.getState().deceased;

	const { dynamicCards: storeDynamicCards } = store.getState().deceased;
	const dynamicCards = safePatientList?.dynamicCards ?? storeDynamicCards;
	const customTabs = safePatientList?.customTabs ?? [];
	const customCombineTabData = safePatientList?.customCombineTabData ?? [];

	let censusAverage = dbData?.censusAverage;
	if (!forComparison) {
		censusAverage = await getDynamicPercentageBy(dbData);
	}

	let lockedTotalModified = lockedTotal;
	let isComparingAgainstAvgCensus = true;

	const transferTypeConsumesLevel = transferType && transferType !== TYPES.ALL;
	const activeFiltersCount = priorityData ? priorityData.filter(e => e.type !== "census" && e.type !== "total" && e.type !== TYPES.ALL && !e.type?.toLowerCase().includes("total")).length : 0;

	let totalFilterData = {
		originalData: baseList,
		totalType: !forComparison ? (transferTypeConsumesLevel || activeFiltersCount > 0) ? TOTAL_TYPE.FILTER : TOTAL_TYPE.MAIN : TOTAL_TYPE.MAIN,
		totalForPercentage: !transferType ? censusAverage : patientFilterData.length,
	};
	let mainNumPercentage = censusAverage;

	await batch(async () => {
		if (transferType) {
			mainNumPercentage = null;
			isComparingAgainstAvgCensus = false;
			totalFilterData.totalForPercentage = patientFilterData.length;
		}

		const newPayload = [];
		const facilityPercentageSelector = forComparison
			? store.getState().deceasedComparison.facilityPercentage
			: store.getState().admission.facilityPercentage;
		if (!_.isEqual(facilityPercentageSelector, newPayload)) {
			forComparison
				? store.dispatch(setFacilityPercentageComparison(newPayload))
				: store.dispatch(setFacilityPercentage(newPayload));
		}

		if (!lockedTotalModified) {
			const newSetIsCensusTotalLocked = false;
			const isCensusTotalLockedSelector = forComparison
				? store.getState().deceasedComparison.isCensusTotalLocked
				: store.getState().admission.isCensusTotalLocked;
			if (!_.isEqual(isCensusTotalLockedSelector, newSetIsCensusTotalLocked)) {
				forComparison
					? store.dispatch(setIsCensusTotalLockedComparison(newSetIsCensusTotalLocked))
					: store.dispatch(setIsCensusTotalLocked(newSetIsCensusTotalLocked));
			}

			const newLockedTotalBy = null;
			const lockedTotalBySelector = forComparison
				? store.getState().deceasedComparison.lockedTotalBy
				: store.getState().admission.lockedTotalBy;
			if (!_.isEqual(lockedTotalBySelector, newLockedTotalBy)) {
				forComparison
					? store.dispatch(setLockedTotalByComparison(newLockedTotalBy))
					: store.dispatch(setLockedTotalBy(newLockedTotalBy));
			}
		}

		if (lockedTotalBy) {
			const newLockedTotalBy = null;
			const newLockTotal = null;
			if (lockedTotalBy === TYPES.ALL || lockedTotalBy === "census") {
				if (lockedTotalModified && transferType !== lockedTotalBy && lockedTotalBy !== "census") {
					const lockedTotalBySelector = forComparison
						? store.getState().deceasedComparison.lockedTotalBy
						: store.getState().admission.lockedTotalBy;
					if (!_.isEqual(lockedTotalBySelector, newLockedTotalBy)) {
						forComparison
							? store.dispatch(setLockedTotalByComparison(newLockedTotalBy))
							: store.dispatch(setLockedTotalBy(newLockedTotalBy));
					}

					const lockedTotalSelector = forComparison
						? store.getState().deceasedComparison.lockedTotal
						: store.getState().admission.lockedTotal;
					if (!_.isEqual(lockedTotalSelector, newLockTotal)) {
						forComparison
							? store.dispatch(setLockTotalComparison(newLockTotal))
							: store.dispatch(setLockTotal(newLockTotal));
					}
					lockedTotalModified = null;
				}
			} else {
				const lockedFilterRemoved = _.find(priorityData, { type: lockedTotalBy });
				if (!lockedFilterRemoved) {
					const lockedTotalBySelector = forComparison
						? store.getState().deceasedComparison.lockedTotalBy
						: store.getState().admission.lockedTotalBy;
					if (!_.isEqual(lockedTotalBySelector, newLockedTotalBy)) {
						forComparison
							? store.dispatch(setLockedTotalByComparison(newLockedTotalBy))
							: store.dispatch(setLockedTotalBy(newLockedTotalBy));
					}

					const lockedTotalSelector = forComparison
						? store.getState().deceasedComparison.lockedTotal
						: store.getState().admission.lockedTotal;
					if (!_.isEqual(lockedTotalSelector, newLockTotal)) {
						forComparison
							? store.dispatch(setLockTotalComparison(newLockTotal))
							: store.dispatch(setLockTotal(newLockTotal));
					}
					lockedTotalModified = null;
				}
			}
		}

		if (lockedTotalModified) {
			totalFilterData.totalForPercentage = lockedTotalModified;
		}
		if (lockedTotalModified && priorityData.length === 1 && !transferType) {
			totalFilterData.totalForPercentage = lockedTotalModified;
		}

		if (priorityData?.length > 0) {
			isComparingAgainstAvgCensus = false;
		}

		if (
			(priorityData?.length > 0 && (!lockedTotalBy || !lockedTotal)) ||
			(priorityData?.length > 0 && lockedTotalBy !== "census" && lockedTotalBy !== TYPES.ALL)
		) {
			totalFilterData.totalForPercentage = null;
		}
	});

	let dynamicCardsObj = {};
	let customTabsObj = {};
	
	// Collect intermediate patient sets before each filter is applied (for tooltip only).
	// This does not change any existing calculations; it's read-only data for UI.
	const intermediateTotals = {};
	const cardTitles = {};
	
	// Calculate final level for remaining cards (accounts for transferType and active priority filters)
	const finalLevel = activeFiltersCount + (transferTypeConsumesLevel ? 1 : 0);
	
	if (priorityData?.length > 0) {
		let i = 0;
		let currentValidFilterIndex = 0;
		let previousValidTitle = '';
		// Adjust starting level based on whether transferType is selected
		const levelOffset = transferTypeConsumesLevel ? 1 : 0;
		
		for await (const ele of priorityData) {
			i++;
			if (priorityData.length === i && lockedTotalModified && !lockedTotalBy) {
				forComparison
					? store.dispatch(setLockedTotalByComparison(ele.type))
					: store.dispatch(setLockedTotalBy(ele.type));
			}

			const isTotalLock = ele.type === "census" || ele.type === "total" || ele.type === TYPES.ALL || ele.type?.toLowerCase().includes("total");

			if (!isTotalLock) {
				currentValidFilterIndex++;
				
				// Store snapshot BEFORE applying the current filter so downstream cards can show level totals.
				if (currentValidFilterIndex > 1 && currentValidFilterIndex <= 6) {
					const actualLevel = (currentValidFilterIndex - 1) + levelOffset;
					if (actualLevel >= 1 && actualLevel <= 5) {
						intermediateTotals[`level${actualLevel}`] = _.cloneDeep(patientFilterData);
						cardTitles[`level${actualLevel}`] = previousValidTitle ? `${previousValidTitle} total` : '';
					}
				}
				previousValidTitle = ele?.cardTitle || ele?.type || '';
			}
			
			// Calculate actualLevel for use in downstream card processing
			const actualLevel = currentValidFilterIndex + levelOffset;

		if (ele?.question?.isCustom) {
				const customTabsRes = customTabs.filter((x) => x.accessor === ele.type);
				if (customTabsRes.length > 0) {
					// For combineTab: Always use mainNumPercentage (original data length before filters) to keep percentage consistent
					// For other custom cards: Use mainNumPercentage when i === 1 (first selected card) to preserve original percentage calculation
					// This matches the pattern used for default cards (e.g., insuranceData, doctorData, etc.)
					const isCombineTab = ele?.question?.customTab?.type === CUSTOM_TAB_TYPE.combineTab;
					// For combineTab: Always use mainNumPercentage (original data length before filters)
					// If mainNumPercentage is not available, use originalData.length as fallback (originalData is the unfiltered data)
					// This ensures percentage is always calculated based on original data length, not filtered data
					const originalDataLength = totalFilterData?.originalData?.length || patientFilterData.length;
					const totalForPercentage = isCombineTab 
						? (mainNumPercentage || originalDataLength || totalFilterData.totalForPercentage)
						: (mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage);
					
					const isCheckNewFilters = true;
					customTabsObj = await getCustomTabsCards(
						patientFilterData,
						customTabsRes,
						{ ...totalFilterData, originalData: totalFilterData.originalData || baseList, totalType: TOTAL_TYPE.FILTER, totalForPercentage, intermediateTotals, currentLevel: actualLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }, forComparison },
						{
							ninetyDaysData: safePatientList.ninetyDaysData,
							dynamicCards,
							pageType: PAGE_TYPE.DECEASED,
							diffDashboardPatients,
							forComparison,
							customCombineTabData,
							isCheckNewFilters,
							newSavedFilters: [...newSavedFilters, ele.type]
						}
					);
				}
				objectCustom = { ...objectCustom, ...dynamicCardsObj, ...customTabsObj };
				patientFilterData = filterCustomPatientData(patientFilterData, cardFilter, ele);
			}

			if (ele.type === DECEASED_CARDS_TYPE.INSURANCE_DATA) {
				objectCustom.insuranceData = await insuranceData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.insuranceData = attachLevelTotals(
					objectCustom.insuranceData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.insuranceId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ insuranceId }) =>
					_.includes(cardFilter.insuranceData, insuranceId)
				);
			}

			if (ele.type === DECEASED_CARDS_TYPE.FLOORS_DATA) {
				objectCustom.floorsData = await floorsData(patientFilterData, {
					...totalFilterData,
					totalType: TOTAL_TYPE.MAIN,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.floorsData = attachLevelTotals(
					objectCustom.floorsData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.floorId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ floorId }) =>
					_.includes(cardFilter.floorsData, floorId)
				);
			}

			if (ele.type === DECEASED_CARDS_TYPE.DOCTOR_DATA) {
				objectCustom.doctorData = await doctorData(patientFilterData, {
					...totalFilterData,
					totalType: TOTAL_TYPE.MAIN,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.doctorData = attachLevelTotals(
					objectCustom.doctorData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.doctorId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ doctorId }) =>
					_.includes(cardFilter.doctorData, doctorId)
				);
			}
			if (ele.type === DECEASED_CARDS_TYPE.DAYS_DATA) {
				objectCustom.daysData = await daysData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.daysData = attachLevelTotals(
					objectCustom.daysData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p.day ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ day }) => _.every([_.includes(cardFilter.daysData, day)]));
			}
			if (ele.type === DECEASED_CARDS_TYPE.NINETY_DAYS_DATA) {
				objectCustom.ninetyDaysData = await ninetyDaysDataList(
					safePatientList.ninetyDaysData,
					patientFilterData,
					{
						...totalFilterData,
						totalType: TOTAL_TYPE.MAIN,
						totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
						intermediateTotals,
						currentLevel: actualLevel,
					}
				);
				objectCustom.ninetyDaysData = maybeAttachTotals(
					objectCustom.ninetyDaysData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					i,
					(p) => p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				if (i === priorityData.length) {
					updateFacilityPercentageTotal(patientFilterData, forComparison);
				}
				patientFilterData = await ninetyDaysDataFilter(
					cardFilter.ninetyDaysData,
					patientFilterData,
					safePatientList.ninetyDaysData
				);
			}
			newSavedFilters.push(ele.type);
			
			if (i === priorityData?.length && ele.type !== DECEASED_CARDS_TYPE.NINETY_DAYS_DATA) {
				updateFacilityPercentageTotal(patientFilterData, forComparison);
			}
		}

		const totalCount = lockedTotalModified ? lockedTotalModified : patientFilterData.length;
		const filterTotalSelector = forComparison
			? store.getState().deceasedComparison.filterTotal
			: store.getState().admission.filterTotal;
		if (!_.isEqual(filterTotalSelector ?? 0, totalCount ?? 0)) {
			forComparison
				? store.dispatch(setFilterTotalComparison(totalCount))
				: store.dispatch(setFilterTotal(totalCount));
		}

		const addIfNotIncluded = async (type, fn) => {
			if (!_.includes(newSavedFilters, type)) {
				objectCustom[type] = await fn();
			}
		};

		await addIfNotIncluded(DECEASED_CARDS_TYPE.INSURANCE_DATA, async () => {
			const data = await insuranceData(patientFilterData, { ...totalFilterData, totalForPercentage: totalCount, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 } });
			return attachLevelTotals(
				data,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.insuranceId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		});
		await addIfNotIncluded(DECEASED_CARDS_TYPE.FLOORS_DATA, async () => {
			const data = await floorsData(patientFilterData, { ...totalFilterData, totalForPercentage: totalCount, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 } });
			return attachLevelTotals(
				data,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.floorId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		});
		await addIfNotIncluded(DECEASED_CARDS_TYPE.DAYS_DATA, async () => {
			const data = await daysData(patientFilterData, { ...totalFilterData, totalForPercentage: totalCount, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 } });
			return attachLevelTotals(
				data,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.day ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		});
		await addIfNotIncluded(DECEASED_CARDS_TYPE.DOCTOR_DATA, async () => {
			const data = await doctorData(patientFilterData, { ...totalFilterData, totalForPercentage: totalCount, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 } });
			return attachLevelTotals(
				data,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p.doctorId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		});
		await addIfNotIncluded(DECEASED_CARDS_TYPE.NINETY_DAYS_DATA, async () => {
			const data = await ninetyDaysDataList(safePatientList.ninetyDaysData, patientFilterData, { ...totalFilterData, totalForPercentage: totalCount, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 } });
			return maybeAttachTotals(
				data,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				priorityData?.length || 0,
				(p) => p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		});

		if (dynamicCards.length > 0) {
			for (const item of dynamicCards) {
				if (!_.includes(newSavedFilters, item?.accessor)) {
					let objectCustomRes = {};
					await processDynamicCard(item, patientFilterData, objectCustomRes, {
						...totalFilterData,
						totalForPercentage: totalCount,
						intermediateTotals,
						currentLevel: finalLevel,
						cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
					});
					if (!_.isEmpty(objectCustomRes)) {
						objectCustom[item?.accessor] = objectCustomRes?.[item?.accessor];
					}
				}
			}
		}

		if (customTabs.length > 0) {
			const isCheckNewFilters = true;
			// For combineTab: Always use mainNumPercentage (original data length before filters) to keep percentage consistent
			// Check if any of the custom tabs are combine tabs
			const hasCombineTab = customTabs.some(tab => tab?.type === CUSTOM_TAB_TYPE.combineTab);
			const originalDataLength = totalFilterData?.originalData?.length || patientFilterData.length;
			// For combine tabs, use mainNumPercentage if available, otherwise use originalDataLength
			// For other custom tabs, use totalCount (filtered data length)
			const totalForPercentageForCombineTabs = hasCombineTab 
				? (mainNumPercentage || originalDataLength || totalFilterData.totalForPercentage)
				: totalCount;
			
			const customTabsObjRes = await getCustomTabsCards(
				patientFilterData,
				customTabs,
				{ ...totalFilterData, totalType: TOTAL_TYPE.FILTER, totalForPercentage: totalForPercentageForCombineTabs, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }, forComparison },
				{
					ninetyDaysData: safePatientList.ninetyDaysData,
					dynamicCards,
					newSavedFilters,
					isCheckNewFilters,
					objectCustom,
					pageType: PAGE_TYPE.DECEASED,
					diffDashboardPatients,
					forComparison,
					customCombineTabData
				}
			);
			objectCustom = { ...objectCustom, ...customTabsObjRes };
		}

		if (!lockedTotalModified) {
			const lockedByFacilitySelector = forComparison
				? store.getState().deceasedComparison.lockedByFacility
				: store.getState().admission.lockedByFacility;
			if (!_.isEqual(lockedByFacilitySelector ?? [], patientFilterData ?? [])) {
				forComparison
					? store.dispatch(setLockedByFacilityComparison(patientFilterData))
					: store.dispatch(setLockedByFacility(patientFilterData));
			}
		}
	} else {
		await batch(async () => {
			let totalCount;
			if (mainNumPercentage) {
				totalCount = mainNumPercentage;
			} else if (transferType && transferType.length > 0) {
				totalCount = patientFilterData.length;
			} else {
				totalCount = censusAverage;
			}
			const filterTotal = lockedTotalModified ? lockedTotalModified : totalCount;
			forComparison
				? store.dispatch(setFilterTotalComparison(filterTotal))
				: store.dispatch(setFilterTotal(filterTotal));

			if (lockedTotalModified && !lockedTotalBy) {
				if (transferType) {
					forComparison
						? store.dispatch(setLockedTotalByComparison(transferType))
						: store.dispatch(setLockedTotalBy(transferType));
				} else {
					forComparison
						? store.dispatch(setLockedTotalByComparison("census"))
						: store.dispatch(setLockedTotalBy("census"));
				}
			}
			if (!transferType && lockedTotalModified) {
				const isCensusTotalLocked = true;
				const isCensusTotalLockedSelector = forComparison
					? store.getState().deceasedComparison.isCensusTotalLocked
					: store.getState().admission.isCensusTotalLocked;
				if (!_.isEqual(isCensusTotalLockedSelector, isCensusTotalLocked)) {
					forComparison
						? store.dispatch(setIsCensusTotalLockedComparison(isCensusTotalLocked))
						: store.dispatch(setIsCensusTotalLocked(isCensusTotalLocked));
				}
			}
			if (!lockedTotalModified) {
				const lockedByFacilitySelector = forComparison
					? store.getState().deceasedComparison.lockedByFacility
					: store.getState().admission.lockedByFacility;
				if (!_.isEqual(lockedByFacilitySelector ?? [], patientFilterData ?? [])) {
					forComparison
						? store.dispatch(setLockedByFacilityComparison(patientFilterData))
						: store.dispatch(setLockedByFacility(patientFilterData));
				}
			}
			updateFacilityPercentageTotal(patientFilterData, forComparison);
		});
		
		[
			objectCustom.insuranceData,
			objectCustom.floorsData,
			objectCustom.doctorData,
			objectCustom.ninetyDaysData,	
			objectCustom.daysData
		] = await Promise.all([
			insuranceData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
			floorsData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
			doctorData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
			ninetyDaysDataList(safePatientList.ninetyDaysData, patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 }),
			daysData(patientFilterData, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 })
		]);		
		if (dynamicCards.length > 0) {
			dynamicCardsObj = await dynamicCardFilter(patientFilterData, dynamicCards, { ...totalFilterData, intermediateTotals: {}, currentLevel: 0 });
		}
		if (customTabs.length > 0) {
			customTabsObj = await getCustomTabsCards(
				patientFilterData,
				customTabs,
				{ ...totalFilterData, intermediateTotals: {}, currentLevel: 0, cardTitles: {}, forComparison },
				{
					ninetyDaysData: safePatientList.ninetyDaysData,
					dynamicCards,
					pageType: PAGE_TYPE.DECEASED,
					diffDashboardPatients,
					forComparison,
					customCombineTabData
				}
			);
		}
		objectCustom = { ...objectCustom, ...dynamicCardsObj, ...customTabsObj };
	}
	objectCustom.isComparingAgainstAvgCensus = isComparingAgainstAvgCensus;

	// Page converter uses main dashboard only (comparison runs do not attach this field).
	if (!forComparison) {
		const pageConverterRows = Array.isArray(patientFilterData) ? patientFilterData : [];
		return {
			...objectCustom,
			pageConverterFilteredPatients: _.cloneDeep(pageConverterRows),
		};
	}
	return objectCustom;
}

async function ninetyDaysDataFilter(cardFilter, patientData, ninetyDaysData) {
	if (!Array.isArray(cardFilter) || !Array.isArray(patientData) || !Array.isArray(ninetyDaysData)) {
		return [];
	}

	// Collect all patient IDs from matching ninetyDaysData entries
	const matchingIds = new Set(
		ninetyDaysData
			.filter(item => cardFilter.includes(item._id) && Array.isArray(item.ids))
			.flatMap(item => item.ids)
	);

	// Return patients whose _id is in the matchingIds set
	return patientData.filter(patient => matchingIds.has(patient._id));
}

async function ninetyDaysDataList(data, patients, totalFilter) {
	const patientIds = Array.isArray(patients) ? patients.map(({ id }) => id) : [];
	const percentageTotal = totalFilter?.totalForPercentage ?? patientIds.length;

	const latestData = await updateListTotalValue(data, patientIds, "value", percentageTotal, totalFilter);
	return latestData;
}
